/* eslint-disable jsx-a11y/accessible-emoji */
/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import * as React from "react";
import DOMPurify from "dompurify";
import { I18n } from "i18n-js";
import styled from "styled-components";
import { datadogRum } from "@datadog/browser-rum";
import * as locales from "../../../../locales.json";

import * as API from "../../../../api/billing";
import Button from "../../../reusable/button";
import Card from "../../../reusable/cardv2";
import ErrorMessage from "../../../reusable/errorMessage";
import FeesModal from "./feesModal";
import InfoText from "../../../reusable/infoText";
import SwitchPlanModal from "./switchPlanModal";
import Tooltip from "../../../reusable/tooltip";
import {
  BillingType,
  RewardBillingCardPropTypes,
  RewardBillingData,
} from "../lib/types";
import {
  StyledHelperText,
  StyledInfoImg,
  StyledLink,
  StyledLinkSpan,
  StyledPaymentMethodWrapper,
  StyledTable,
  StyledTableData,
  StyledTableHeader,
  StyledTableRow,
} from "../lib/styles";
import {
  defaultRewardBillingData,
  genericErrorMessage,
} from "../lib/constants";
import { convertCurrencyToIntString } from "../../../../utils";
import { redesignTheme } from "../../../theme/theme";
import FlexSettingsModal from "./flexSettingsModal";
import FlexPaymentModal from "./flexPaymentModal";
import FlexConfirmModal from "./flexConfirmModal";
import PaymentMethod from "./paymentMethod";

const i18n = new I18n(locales);
i18n.defaultLocale = "en";
i18n.locale = (window as any).locale || "";

const infoCircle = require("../../../svg/infoCircle.svg");

const StyledDiv = styled.div`
  margin-top: ${redesignTheme.spacing[4.5]};
`;

const StyledTextContent = styled.div`
  font-size: ${redesignTheme.fontSize[100]};
  color: ${redesignTheme.color.gray900};
  line-height: 24px;
  & a {
    color: ${redesignTheme.color.blue500};
    font-weight: ${redesignTheme.fontWeight.medium};
    text-decoration: none;
    &:hover {
      color: ${redesignTheme.color.blue700};
    }
  }
`;

const StyledPrepayButton = styled(Button)`
  background-color: ${redesignTheme.color.blueAlpha100};
  margin-top: ${redesignTheme.spacing[2.5]};
  &:hover {
    background-color: ${redesignTheme.color.blueAlpha200};
  }
`;

const StyledFloatingAction = styled(StyledLinkSpan)`
  display: flex;
  gap: 6px;
  position: absolute;
  right: 20px;
  top: 24px;
  a {
    position: relative;
    bottom: 2px;
  }
`;

const RewardBillingCard = ({
  billingType,
  card,
  openPortal,
  fetchPaymentMethodData,
  paymentDueOn,
  refetchData,
  refillAmount,
  refillThreshold,
  setPageLoading,
  setRefetchData,
  setSuccessMessage,
  showSuperAdminFeature,
  subscriptionAutoCollection,
  successMessage,
  updateToastMessage,
}: RewardBillingCardPropTypes): React.ReactElement => {
  const [data, setData] = React.useState<RewardBillingData>(
    defaultRewardBillingData
  );
  const { trialing } = data;

  const [loading, setLoading] = React.useState(true);
  const [errorMessage, setErrorMessage] = React.useState();

  const [modal, setModal] = React.useState("");

  const [amount, setAmount] = React.useState("500");

  const closeModal = (): void => {
    return setModal("");
  };

  const fetchData = async (): Promise<void> => {
    setLoading(true);
    setData(defaultRewardBillingData);

    try {
      const response = await API.getRewardBilling();
      const { data } = response.data;
      setData(data);
      const amount = convertCurrencyToIntString(refillAmount);

      setAmount(amount);
    } catch (error) {
      datadogRum.addError(error);
      setData(defaultRewardBillingData);
      setErrorMessage(genericErrorMessage);
    } finally {
      setLoading(false);
      setRefetchData(false);
    }
  };

  const openPortalCallback = (): void => {
    fetchData();
    fetchPaymentMethodData("payment");
  };

  React.useEffect(() => {
    fetchData();
  }, []);

  React.useEffect(() => {
    if (refetchData) {
      fetchData();
    }
  }, [refetchData]);

  return (
    <Card
      loading={loading}
      testId="reward-billing"
      subtitle={
        <span
          style={{
            ...(trialing ? { color: redesignTheme.color.green600 } : {}),
          }}
        >
          {data.displayBillingType}
        </span>
      }
      successMessage={successMessage}
      title={i18n.translate("title.reward_settings")}
    >
      {errorMessage ? (
        <div>
          <ErrorMessage
            errorMessage={errorMessage}
            testId="reward-billing-error"
          />
        </div>
      ) : (
        <>
          {trialing && billingType === BillingType.None ? (
            <>
              <StyledFloatingAction>
                <a
                  href="https://bonusly.com/rewards-calculator"
                  rel="noreferrer"
                  target="_blank"
                >
                  {i18n.translate(
                    "admin_pages.settings.billing.budgeting_calculator"
                  )}
                </a>
                <Tooltip
                  content={i18n.translate(
                    "admin_pages.settings.billing.budgeting_calculator_tooltip"
                  )}
                  placement="top"
                >
                  <StyledInfoImg src={infoCircle} alt="info circle" />
                </Tooltip>
              </StyledFloatingAction>

              <StyledTextContent
                data-testid="trialing-message"
                style={{ display: "flex", flexDirection: "column", gap: "6px" }}
              >
                <strong>
                  {i18n.translate(
                    "admin_pages.settings.billing.trial_account_purchase"
                  )}
                </strong>
                {i18n.translate(
                  "admin_pages.settings.billing.trial_account_get_started"
                )}
                <br />
                <StyledLinkSpan style={{ fontSize: "14px", marginTop: "4px" }}>
                  {i18n.translate(
                    "admin_pages.settings.billing.trial_account_switch"
                  )}
                </StyledLinkSpan>
              </StyledTextContent>
            </>
          ) : (
            <StyledTextContent
              style={{
                marginBottom:
                  billingType !== BillingType.Fixed &&
                  billingType !== BillingType.None
                    ? redesignTheme.spacing[1.5]
                    : redesignTheme.spacing[4.5],
              }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  i18n.translate(
                    `admin_pages.settings.billing.${billingType}_description`,
                    {
                      refillAmount,
                      refillThreshold,
                    }
                  ),
                  { USE_PROFILES: { html: true } }
                ),
              }}
            />
          )}

          {billingType === BillingType.None && (
            <Button
              onClick={(event): void => {
                window.amplitude?.track("Add Credit Card Modal Opened");
                return openPortal(event, "PAYMENT_SOURCES", openPortalCallback);
              }}
              size="small"
              testId="add-credit-card-button"
              variant="primary"
            >
              {i18n.translate("admin_pages.settings.billing.add_credit_card")}
            </Button>
          )}

          {billingType !== BillingType.None && (
            <>
              {(billingType === BillingType.PayAsYouGo ||
                billingType === BillingType.Flex) && (
                <div
                  data-testid="fees-message"
                  style={{ marginBottom: redesignTheme.spacing[4.5] }}
                >
                  <StyledHelperText>
                    {i18n.translate("admin_pages.settings.billing.plan_fee", {
                      fee: billingType === BillingType.PayAsYouGo ? "8" : "5",
                    })}
                  </StyledHelperText>{" "}
                  <StyledLink
                    data-testid="learn-more-fees-link"
                    onClick={() => {
                      return setModal("fees");
                    }}
                  >
                    {i18n.translate("admin_pages.settings.billing.learn_more")}
                  </StyledLink>
                </div>
              )}

              <StyledTable>
                <tbody>
                  <StyledTableRow>
                    <StyledTableHeader>
                      {i18n.translate(
                        "admin_pages.settings.billing.bill_amount"
                      )}
                    </StyledTableHeader>
                    <StyledTableData data-testid="bill-amount">
                      {billingType === BillingType.PayAsYouGo ? (
                        <>
                          {i18n.translate(
                            "admin_pages.settings.billing.varies"
                          )}{" "}
                          <Tooltip
                            content={i18n.translate(
                              "admin_pages.settings.billing.varies_tooltip"
                            )}
                            placement="top"
                          >
                            <StyledInfoImg src={infoCircle} alt="info circle" />
                          </Tooltip>
                        </>
                      ) : (
                        refillAmount
                      )}
                    </StyledTableData>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableHeader>
                      {i18n.translate(
                        "admin_pages.settings.billing.rewards_postpay_threshold"
                      )}{" "}
                      <Tooltip
                        content={i18n.translate(
                          `admin_pages.settings.billing.${billingType}_threshold_tooltip`
                        )}
                        placement="top"
                      >
                        <StyledInfoImg src={infoCircle} alt="info circle" />
                      </Tooltip>
                    </StyledTableHeader>
                    <StyledTableData data-testid="billing-threshold">
                      {billingType === BillingType.PayAsYouGo
                        ? `-${refillThreshold}`
                        : refillThreshold}
                    </StyledTableData>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableHeader>
                      {i18n.translate(
                        "admin_pages.settings.billing.current_rewards_balance"
                      )}{" "}
                    </StyledTableHeader>
                    <StyledTableData data-testid="rewards-balance">
                      {data.rewardBalance}
                    </StyledTableData>
                  </StyledTableRow>
                </tbody>
              </StyledTable>

              {billingType === BillingType.PayAsYouGo && (
                <StyledPrepayButton
                  onClick={(): void => {
                    window.amplitude?.track(
                      "Billing Settings: Setup Prepay (start)"
                    );

                    setSuccessMessage("");

                    setModal("flexSettings");
                  }}
                  size="small"
                  testId="switch-to-flex-button"
                  variant="primaryInverse"
                >
                  {i18n.translate(
                    "admin_pages.settings.billing.switch_to_flex"
                  )}
                </StyledPrepayButton>
              )}

              {billingType === BillingType.Flex && (
                <Button
                  onClick={(): void => {
                    setSuccessMessage("");
                    setModal("flexSettings");
                  }}
                  size="small"
                  testId="modify-plan-button"
                  variant="primary"
                >
                  {i18n.translate(
                    "admin_pages.settings.billing.modify_plan_settings"
                  )}
                </Button>
              )}

              {(billingType === BillingType.Flex ||
                billingType === BillingType.Fixed) &&
                showSuperAdminFeature && (
                  <div
                    style={{
                      marginTop:
                        billingType === BillingType.Flex
                          ? redesignTheme.spacing[4.5]
                          : "0",
                    }}
                  >
                    <Button
                      onClick={(): void => {
                        setSuccessMessage("");
                        setModal("switchPlan");
                      }}
                      size="small"
                      testId="switch-to-pay-as-you-go-button"
                      variant="superAdmin"
                    >
                      {i18n.translate(
                        "admin_pages.settings.billing.switch_to_pay_as_you_go"
                      )}
                    </Button>
                  </div>
                )}
            </>
          )}

          {billingType !== BillingType.None && (
            <StyledDiv>
              <InfoText>
                <StyledLinkSpan
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      i18n.translate(
                        "admin_pages.settings.billing.learn_about_billing_plans"
                      )
                    ).replace("href", "target='_blank' rel='noreferrer' href"),
                  }}
                />
              </InfoText>
            </StyledDiv>
          )}

          {billingType !== BillingType.None && (
            <StyledPaymentMethodWrapper>
              <PaymentMethod
                cardType={card.type}
                expiration={card.expiration}
                last4={card.last4}
                openPortal={openPortal}
                paymentDueOn={paymentDueOn}
                refillAmount={refillAmount}
                refillThreshold={refillThreshold}
                rewardBillingType={billingType}
                setPageLoading={setPageLoading}
                setRefetchData={setRefetchData}
                subscriptionAutoCollection={subscriptionAutoCollection}
                type="reward"
              />
            </StyledPaymentMethodWrapper>
          )}
        </>
      )}

      <FeesModal closeModal={closeModal} modal={modal} />

      <FlexSettingsModal
        amount={amount}
        closeModal={closeModal}
        existingAmount={refillAmount}
        isModifying={billingType === BillingType.Flex}
        modal={modal}
        setAmount={setAmount}
        setModal={setModal}
        userCount={data.userCount}
      />

      <FlexConfirmModal
        card={card}
        closeModal={closeModal}
        modal={modal}
        setModal={setModal}
      />

      <FlexPaymentModal
        amount={amount}
        closeModal={closeModal}
        companyId={data.companyId}
        fetchData={() => {
          return setRefetchData(true);
        }}
        isModifying={billingType === BillingType.Flex}
        modal={modal}
        rewardBalance={data.rewardBalance}
        setLoading={setLoading}
        setSuccessMessage={setSuccessMessage}
        updateToastMessage={updateToastMessage}
      />

      <SwitchPlanModal
        closeModal={closeModal}
        companyId={data.companyId}
        fetchData={() => {
          return setRefetchData(true);
        }}
        modal={modal}
        updateToastMessage={updateToastMessage}
      />
    </Card>
  );
};

export default RewardBillingCard;
